export type FrontendChangelogEntry = {
  id: string;
  title: string;
  date?: string;
  short_description?: string; // New field expected in JSON
  summary?: string; // legacy fallback
  tags?: string[];
};

// Eagerly import all JSON files from the symlinked changelog directory
// The symlink exists at src/changelog -> ../../changelog
const modules = import.meta.glob<{ default: FrontendChangelogEntry }>(
  "../changelog/*.json",
  { eager: true }
);

function filenameToId(filename: string): string {
  const match = filename.match(/([^/]+)\.json$/);
  return match ? match[1] : filename;
}

export function getAllChangelogEntries(): FrontendChangelogEntry[] {
  const entries: FrontendChangelogEntry[] = [];
  for (const [path, mod] of Object.entries(modules)) {
    const data = (mod as any).default ?? (mod as any);
    if (!data || typeof data !== "object") continue;
    const id = data.id ?? filenameToId(path);
    const title = (data as any).title as string | undefined;
    // Prefer short_description; fall back to summary
    const short_description = (data as any).short_description as string | undefined;
    const summary = (data as any).summary as string | undefined;
    if (!title) continue;
    entries.push({
      id,
      title,
      date: (data as any).date,
      short_description: short_description ?? summary,
      summary,
      tags: (data as any).tags,
    });
  }
  // Sort newest first by ID (expects YYYYMMDD prefix) then by date if provided
  entries.sort((a, b) => (b.id || "").localeCompare(a.id || ""));
  return entries;
}

export function getLatestChangelogEntries(limit = 5): FrontendChangelogEntry[] {
  return getAllChangelogEntries().slice(0, limit);
}

export function marketingChangelogUrlForId(id: string): string {
  // Use hash anchors to jump to the exact item on the marketing site
  return `https://podcast-generator.ai/changelog#${id}`;
}

export function hasRecentChangelog(maxDaysOld = 7): boolean {
  const entries = getAllChangelogEntries();
  if (entries.length === 0) return false;
  
  const latestEntry = entries[0];
  if (!latestEntry.date) return false;
  
  const entryDate = new Date(latestEntry.date);
  const now = new Date();
  const daysDiff = (now.getTime() - entryDate.getTime()) / (1000 * 60 * 60 * 24);
  
  return daysDiff <= maxDaysOld;
}

// Helpers to compute how many changelog entries were added in the last N days
function parseDateFromIdOrField(entry: FrontendChangelogEntry): Date | null {
  if (entry.date) {
    const d = new Date(entry.date);
    return isNaN(d.getTime()) ? null : d;
  }
  // Fallback: attempt to parse first 8 digits (YYYYMMDD) from id
  const m = entry.id?.match(/^(\d{4})(\d{2})(\d{2})/);
  if (m) {
    const [_, y, mo, d] = m;
    const dt = new Date(Number(y), Number(mo) - 1, Number(d));
    return isNaN(dt.getTime()) ? null : dt;
  }
  return null;
}

export function getRecentChangelogCount(maxDaysOld = 14): number {
  const now = new Date();
  const entries = getAllChangelogEntries();
  const lastSeenId = getLastSeenChangelogId();

  return entries.filter((e) => {
    // Only include entries strictly newer than the last seen changelog id (if present)
    if (lastSeenId && e.id.localeCompare(lastSeenId) <= 0) return false;

    // Still respect the recency window to avoid showing very old items
    const d = parseDateFromIdOrField(e);
    if (!d) return false;
    const diffDays = (now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays <= maxDaysOld;
  }).length;
}

// Variant that ignores whether the user has seen the items; counts all entries within window.
export function getRecentChangelogTotal(maxDaysOld = 30): number {
  const now = new Date();
  return getAllChangelogEntries().filter(e => {
    const d = parseDateFromIdOrField(e);
    if (!d) return false;
    const diffDays = (now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays <= maxDaysOld;
  }).length;
}

export function recentChangelogLabel(count: number): string {
  const n = Math.max(0, Math.floor(count));
  const noun = n === 1 ? 'feature' : 'features';
  return `${n} new ${noun}`;
}

// Badge visibility helpers: show the badge only when the latest changelog ID
// differs from what the user last saw. This ignores time windows and purely
// compares IDs so that once a user opens the popover, the badge hides until
// the newest entry changes (even if it changes to an older/newer ID).
const LAST_SEEN_KEY = 'changelog_last_seen_id';

export function getLatestChangelogId(): string | null {
  const latest = getAllChangelogEntries()[0];
  return latest?.id ?? null;
}

export function getLastSeenChangelogId(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(LAST_SEEN_KEY);
  } catch {
    return null;
  }
}

export function setLastSeenChangelogId(id: string): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(LAST_SEEN_KEY, id);
  } catch {
    // ignore storage failures
  }
}

export function shouldShowChangelogBadge(): boolean {
  const latestId = getLatestChangelogId();
  if (!latestId) return false;
  const lastSeen = getLastSeenChangelogId();
  return lastSeen !== latestId;
}
